// components/layout/Sidebar.jsx
// Enhanced CRM sidebar — brand removed, cleaner filters, full module names, softer labels.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./Sidebar.css";
import { NavLink } from "react-router-dom";
import axios from "axios";
import { baseURL } from "../pages/backend_url";
import { canViewModule } from "../auth/moduleAccess";

import DashboardIcon from "@mui/icons-material/Dashboard";
import ContactPageIcon from "@mui/icons-material/ContactPage";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import EmailIcon from "@mui/icons-material/Email";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import SearchIcon from "@mui/icons-material/Search";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import KeyboardArrowLeftRoundedIcon from "@mui/icons-material/KeyboardArrowLeftRounded";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import ManageAccountsRoundedIcon from "@mui/icons-material/ManageAccountsRounded";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import ArchiveRoundedIcon from "@mui/icons-material/ArchiveRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import AppsRoundedIcon from "@mui/icons-material/AppsRounded";
import InboxRoundedIcon from "@mui/icons-material/InboxRounded";
import HubRoundedIcon from "@mui/icons-material/HubRounded";
import ShieldRoundedIcon from "@mui/icons-material/ShieldRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";

const COLLAPSED_STORAGE_KEY = "crm_sidebar_collapsed";
const TAB_STORAGE_KEY = "crm_sidebar_tab";
const SEARCH_STORAGE_KEY = "crm_sidebar_search";
const ALLOWED_TABS = ["all", "fav"];

const normalizeText = (value) => String(value || "").trim().toLowerCase();

const safeJsonParse = (raw, fallback = null) => {
  try {
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const getLoggedUserFromSession = () => {
  return safeJsonParse(sessionStorage.getItem("user_token"), null);
};

const isDarkTheme = (theme) => {
  if (theme === true) return true;
  const value = String(theme || "").toLowerCase();
  return value.includes("dark") || value === "1";
};

const getInitialCollapsed = () => {
  try {
    return localStorage.getItem(COLLAPSED_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};

const getInitialTab = () => {
  try {
    const saved = localStorage.getItem(TAB_STORAGE_KEY) || "all";
    return ALLOWED_TABS.includes(saved) ? saved : "all";
  } catch {
    return "all";
  }
};

const getInitialSearch = () => {
  try {
    return localStorage.getItem(SEARCH_STORAGE_KEY) || "";
  } catch {
    return "";
  }
};

const useCompactSidebarViewport = () => {
  const query = "(max-width: 900px)";
  const [compact, setCompact] = useState(() => (
    typeof window !== "undefined" && window.matchMedia?.(query).matches
  ));

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;

    const media = window.matchMedia(query);
    const update = () => setCompact(media.matches);
    update();
    media.addEventListener?.("change", update);
    return () => media.removeEventListener?.("change", update);
  }, []);

  return compact;
};

const routeForModule = (name) => {
  const clean = String(name || "").trim();
  if (!clean) return "/Dashboard";
  return `/${clean.replace(/\s+/g, "")}`;
};

const getInitials = (value) => {
  const text = String(value || "").trim();
  if (!text) return "U";
  if (text.includes("@")) return text.slice(0, 2).toUpperCase();
  const parts = text.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
};

const titleCaseRole = (role) => {
  const raw = String(role || "User").replace(/_/g, " ").trim();
  if (!raw) return "User";
  if (raw.toLowerCase() === "superadmin") return "SuperAdmin";
  return raw.replace(/\b\w/g, (char) => char.toUpperCase());
};

const MODULE_ICON_MAP = {
  dashboard: DashboardIcon,
  "von-hub interface": HubRoundedIcon,
  "call center interface": ContactPageIcon,
  "customer search": PersonSearchIcon,
  "calls info": QueryStatsIcon,
  "call centre audit": HistoryRoundedIcon,
  "locations info": LocationOnRoundedIcon,
  "hubspot info": EmailIcon,
  "hubspot archive": ArchiveRoundedIcon,
  "access setup": ShieldRoundedIcon,
  "users setup": ManageAccountsRoundedIcon,
  "agents setup": SupportAgentRoundedIcon,
  "catalog setup": Inventory2RoundedIcon,
  "unified crm": AppsRoundedIcon,
  "email tickets": InboxRoundedIcon,
  "email ticket audit": HistoryRoundedIcon,
  "system audit": ShieldRoundedIcon,
  "email survey": EmailIcon,
  "location workspace": LocationOnRoundedIcon,
  "location oversight": QueryStatsIcon,
};

const getIconComponent = (moduleName) => {
  const name = normalizeText(moduleName);

  if (MODULE_ICON_MAP[name]) return MODULE_ICON_MAP[name];
  if (name.includes("email") || name.includes("ticket")) return EmailIcon;
  if (name.includes("hubspot")) return EmailIcon;
  if (name.includes("user")) return ManageAccountsRoundedIcon;
  if (name.includes("agent")) return SupportAgentRoundedIcon;
  if (name.includes("access") || name.includes("role") || name.includes("permission")) return ShieldRoundedIcon;
  if (name.includes("catalog") || name.includes("setup")) return TuneRoundedIcon;
  if (name.includes("location")) return LocationOnRoundedIcon;
  if (name.includes("call") || name.includes("contact")) return ContactPageIcon;
  if (name.includes("search")) return PersonSearchIcon;
  if (name.includes("report") || name.includes("stats") || name.includes("analytics")) return QueryStatsIcon;

  return DashboardIcon;
};

const getModuleMeta = (moduleName) => {
  const name = normalizeText(moduleName);

  if (name === "dashboard") {
    return {
      category: "Overview",
      description: "Operational overview",
      priority: 0,
    };
  }

  if (name.includes("email ticket audit")) {
    return {
      category: "Customer Care",
      description: "Ticket audit & agent performance",
      priority: 11,
    };
  }

  if (name.includes("email survey")) {
    return {
      category: "Customer Care",
      description: "Rental-experience feedback",
      priority: 12,
    };
  }

  if (name.includes("email tickets")) {
    return {
      category: "Customer Care",
      description: "Email ticketing workspace",
      priority: 10,
    };
  }

  if (name.includes("location workspace")) {
    return {
      category: "Customer Care",
      description: "Restricted location collaboration",
      priority: 10.5,
    };
  }

  if (name.includes("location oversight")) {
    return {
      category: "Customer Care",
      description: "SuperAdmin location-work oversight",
      priority: 10.6,
    };
  }

  if (name.includes("hubspot archive")) {
    return {
      category: "Customer Care",
      description: "Archived HubSpot records",
      priority: 14,
    };
  }

  if (name.includes("hubspot")) {
    return {
      category: "Customer Care",
      description: "HubSpot email visibility",
      priority: 13,
    };
  }

  if (name.includes("unified crm")) {
    return {
      category: "Operations",
      description: "Cases, queues, search",
      priority: 20,
    };
  }

  if (name.includes("call center")) {
    return {
      category: "Operations",
      description: "Call center workflow",
      priority: 21,
    };
  }

  if (name.includes("von")) {
    return {
      category: "Operations",
      description: "Vonage interface",
      priority: 22,
    };
  }

  if (name.includes("customer search")) {
    return {
      category: "Operations",
      description: "Customer lookup",
      priority: 23,
    };
  }

  if (name.includes("calls info")) {
    return {
      category: "Operations",
      description: "Call analytics",
      priority: 24,
    };
  }

  if (name.includes("call centre audit")) {
    return {
      category: "Operations",
      description: "Call performance & quality audit",
      priority: 25,
    };
  }

  if (name.includes("system audit")) {
    return {
      category: "Administration",
      description: "Product-wide change evidence",
      priority: 36,
      displayName: "Audit",
    };
  }

  if (name.includes("location")) {
    return {
      category: "Administration",
      description: "Location management",
      priority: 31,
    };
  }

  if (name.includes("access")) {
    return {
      category: "Administration",
      description: "Roles and permissions",
      priority: 32,
    };
  }

  if (name.includes("users")) {
    return {
      category: "Administration",
      description: "User management",
      priority: 33,
    };
  }

  if (name.includes("agents")) {
    return {
      category: "Administration",
      description: "Agent setup",
      priority: 34,
    };
  }

  if (name.includes("catalog")) {
    return {
      category: "Administration",
      description: "CRM dropdown catalog",
      priority: 35,
    };
  }

  return {
    category: "Modules",
    description: "CRM module",
    priority: 50,
  };
};

const CATEGORY_ORDER = {
  Overview: 0,
  "Customer Care": 1,
  Operations: 2,
  Administration: 3,
  Modules: 4,
};

const sortModules = (items) => {
  return [...(items || [])].sort((a, b) => {
    const aName = normalizeText(a?.name);
    const bName = normalizeText(b?.name);

    if (aName === "dashboard" && bName !== "dashboard") return -1;
    if (aName !== "dashboard" && bName === "dashboard") return 1;

    const aCat = CATEGORY_ORDER[a?.category] ?? 99;
    const bCat = CATEGORY_ORDER[b?.category] ?? 99;
    if (aCat !== bCat) return aCat - bCat;

    const aPriority = Number(a?.priority ?? 99);
    const bPriority = Number(b?.priority ?? 99);
    if (aPriority !== bPriority) return aPriority - bPriority;

    return String(a?.name || "").localeCompare(String(b?.name || ""));
  });
};

const dedupeModules = (modules) => {
  const seen = new Set();
  const out = [];

  for (const item of modules || []) {
    const key = normalizeText(item?.name);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }

  return out;
};

function SidebarSkeleton({ collapsed }) {
  return (
    <div className="sidebar-skeleton-list" aria-hidden="true">
      {Array.from({ length: 7 }).map((_, index) => (
        <div className="sidebar-skeleton-item" key={index}>
          <span className="sidebar-skeleton-icon" />
          {!collapsed && (
            <span className="sidebar-skeleton-copy">
              <span />
              <span />
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

export default function Sidebar({ setCollapsedFlag, theme }) {
  const searchInputRef = useRef(null);

  const [collapsed, setCollapsed] = useState(getInitialCollapsed);
  const [compactExpanded, setCompactExpanded] = useState(false);
  const [search, setSearch] = useState(getInitialSearch);
  const [menuItems, setMenuItems] = useState([]);
  const [tab, setTab] = useState(getInitialTab);
  const [loading, setLoading] = useState(true);
  const [moduleError, setModuleError] = useState("");
  const [savingFavouriteId, setSavingFavouriteId] = useState(null);
  const compactViewport = useCompactSidebarViewport();
  const isCollapsed = compactViewport ? !compactExpanded : collapsed;

  const loggedUser = useMemo(() => getLoggedUserFromSession(), []);

  const userEmail = String(
    loggedUser?.userEmail || loggedUser?.email || loggedUser?.username || ""
  ).trim();

  const role = String(
    loggedUser?.role || loggedUser?.userRole || loggedUser?.accessRole || "User"
  ).trim();

  const userName = String(
    loggedUser?.userName || loggedUser?.name || loggedUser?.fullName || userEmail || "User"
  ).trim();

  const effectiveTheme = isDarkTheme(theme || localStorage.getItem("crm_theme") || localStorage.getItem("td_theme"))
    ? "dark"
    : "light";

  useEffect(() => {
    setCollapsedFlag?.(isCollapsed);
  }, [isCollapsed, setCollapsedFlag]);

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_STORAGE_KEY, String(collapsed));
    } catch {}
  }, [collapsed]);

  useEffect(() => {
    if (compactViewport) setCompactExpanded(false);
  }, [compactViewport]);

  useEffect(() => {
    try {
      localStorage.setItem(TAB_STORAGE_KEY, tab);
    } catch {}
  }, [tab]);

  useEffect(() => {
    if (!ALLOWED_TABS.includes(tab)) {
      setTab("all");
    }
  }, [tab]);

  useEffect(() => {
    try {
      localStorage.setItem(SEARCH_STORAGE_KEY, search);
    } catch {}
  }, [search]);

  const loadModules = useCallback(async () => {
    if (!loggedUser) return;

    try {
      setLoading(true);
      setModuleError("");

      const res = await axios.get(baseURL + "/get_modules", { timeout: 20000 });
      const rawModules = Array.isArray(res.data) ? res.data : [];
      // The API owns the production module catalogue. During local UI work,
      // a synthetic survey item is useful for review, but it must still obey
      // exactly the same role permission gate as every production module.
      const localSurveyPreview =
        process.env.NODE_ENV === "development" &&
        ["localhost", "127.0.0.1"].includes(window.location.hostname);
      const withLocalSurveyPreview =
        localSurveyPreview && !rawModules.some((m) => normalizeText(m?.name) === "email survey")
          ? [...rawModules, { _id: "email-survey-local-preview", name: "Email Survey", favourite: {}, _synthetic: true, _localPreview: true }]
          : rawModules;

      const withDashboard = withLocalSurveyPreview.some((m) => normalizeText(m?.name) === "dashboard")
        ? withLocalSurveyPreview
        : [{ _id: "dashboard", name: "Dashboard", favourite: {}, _synthetic: true }, ...withLocalSurveyPreview];

      const modules = dedupeModules(withDashboard)
        .map((m, idx) => {
          const name = String(m?.name || "").trim();
          const meta = getModuleMeta(name);
          const Icon = getIconComponent(name);

          return {
            id: String(m?._id || name || idx),
            name,
            displayName: meta.displayName || name,
            Icon,
            route: routeForModule(name),
            favourite: m?.favourite && typeof m.favourite === "object" ? m.favourite : {},
            canFavourite: !m?._synthetic,
            category: meta.category,
            description: meta.description,
            priority: meta.priority,
          };
        })
        .filter((module) => canViewModule(loggedUser, module.name));

      setMenuItems(sortModules(modules));
    } catch (err) {
      console.error("MODULE LOAD ERROR:", err);
      setMenuItems([]);
      setModuleError(err?.response?.data?.message || "Could not load CRM modules.");
    } finally {
      setLoading(false);
    }
  }, [loggedUser]);

  useEffect(() => {
    loadModules();
  }, [loadModules]);

  const favouriteCount = useMemo(
    () => menuItems.filter((m) => Boolean(m.favourite?.[userEmail])).length,
    [menuItems, userEmail]
  );

  const visibleItems = useMemo(() => {
    let result = [...menuItems];

    if (tab === "fav") {
      result = result.filter((m) => Boolean(m.favourite?.[userEmail]));
    }

    const q = normalizeText(search);
    if (q) {
      result = result.filter((m) => {
        return [m.name, m.displayName, m.category, m.description]
          .join(" ")
          .toLowerCase()
          .includes(q);
      });
    }

    return sortModules(result);
  }, [menuItems, tab, search, userEmail]);

  const groupedItems = useMemo(() => {
    const groups = [];
    const map = new Map();

    for (const item of visibleItems) {
      const key = item.category || "Modules";
      if (!map.has(key)) {
        const bucket = { name: key, items: [] };
        map.set(key, bucket);
        groups.push(bucket);
      }
      map.get(key).items.push(item);
    }

    return groups.sort((a, b) => (CATEGORY_ORDER[a.name] ?? 99) - (CATEGORY_ORDER[b.name] ?? 99));
  }, [visibleItems]);

  const tabItems = useMemo(
    () => [
      { id: "all", label: "All", count: menuItems.length },
      { id: "fav", label: "Favourites", count: favouriteCount },
    ],
    [menuItems, favouriteCount]
  );

  const toggleCollapsed = useCallback(() => {
    if (compactViewport) {
      setCompactExpanded((expanded) => !expanded);
      return;
    }
    setCollapsed((prev) => !prev);
  }, [compactViewport]);

  const expandAndFocusSearch = useCallback(() => {
    if (compactViewport) setCompactExpanded(true);
    else setCollapsed(false);
    window.setTimeout(() => searchInputRef.current?.focus(), 120);
  }, [compactViewport]);

  const handleClearSearch = useCallback(() => {
    setSearch("");
    searchInputRef.current?.focus();
  }, []);

  const toggleFavourite = useCallback(
    async (event, item) => {
      event.preventDefault();
      event.stopPropagation();

      if (!userEmail || !item?.id || !item?.canFavourite || savingFavouriteId) return;

      const previous = menuItems;
      const current = Boolean(item.favourite?.[userEmail]);
      const nextFavourite = {
        ...(item.favourite || {}),
        [userEmail]: !current,
      };

      const updated = menuItems.map((module) =>
        String(module.id) === String(item.id)
          ? {
              ...module,
              favourite: nextFavourite,
            }
          : module
      );

      setSavingFavouriteId(String(item.id));
      setMenuItems(sortModules(updated));

      try {
        await axios.put(
          baseURL + `/update_favourite/${encodeURIComponent(item.id)}`,
          { favourite: nextFavourite },
          { timeout: 15000 }
        );
      } catch (err) {
        console.error("FAVOURITE UPDATE ERROR:", err);
        setMenuItems(previous);
      } finally {
        setSavingFavouriteId(null);
      }
    },
    [menuItems, userEmail, savingFavouriteId]
  );

  useEffect(() => {
    const onKeyDown = (event) => {
      const tag = String(event.target?.tagName || "").toLowerCase();
      const isTyping = tag === "input" || tag === "textarea" || tag === "select" || event.target?.isContentEditable;

      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        expandAndFocusSearch();
        return;
      }

      if (!isTyping && event.altKey && event.key.toLowerCase() === "b") {
        event.preventDefault();
        toggleCollapsed();
      }

      if (event.key === "Escape") {
        if (compactViewport && compactExpanded) {
          setCompactExpanded(false);
          return;
        }
        if (search) setSearch("");
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [compactExpanded, compactViewport, expandAndFocusSearch, toggleCollapsed, search]);

  if (!loggedUser) return null;

  return (
    <>
      {compactViewport && compactExpanded && (
        <button
          type="button"
          className="sidebar-mobile-backdrop"
          aria-label="Close navigation"
          onClick={() => setCompactExpanded(false)}
        />
      )}
      <aside
      className={`sidebar ${isCollapsed ? "collapsed" : ""}`}
      data-theme={effectiveTheme}
      aria-label="CRM sidebar navigation"
    >
      <div className="sidebar-inner">
        <div className="sidebar-control-row">
          {!isCollapsed && <span className="sidebar-control-title">Navigation</span>}

          {!isCollapsed && (
            <button
              type="button"
              className="sidebar-refresh-btn sidebar-refresh-btn--top"
              onClick={loadModules}
              disabled={loading}
              title="Reload modules"
            >
              <RefreshRoundedIcon className={loading ? "spin" : ""} />
            </button>
          )}

          <button
            type="button"
            className="sidebar-collapse-btn"
            onClick={toggleCollapsed}
            aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {isCollapsed ? <ChevronRightRoundedIcon /> : <KeyboardArrowLeftRoundedIcon />}
          </button>
        </div>

        {!isCollapsed ? (
          <div className="sidebar-search-card">
            <SearchIcon className="sidebar-search-icon" />
            <input
              ref={searchInputRef}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="sidebar-search-input"
              placeholder="Search modules..."
              aria-label="Search CRM modules"
            />
            {search ? (
              <button
                type="button"
                className="sidebar-search-clear"
                onClick={handleClearSearch}
                title="Clear search"
              >
                <ClearRoundedIcon />
              </button>
            ) : (
              <span className="sidebar-search-kbd">Search</span>
            )}
          </div>
        ) : (
          <button type="button" className="sidebar-search-collapsed" onClick={expandAndFocusSearch} title="Search modules">
            <SearchIcon />
          </button>
        )}

        {!isCollapsed && (
          <div className="sidebar-tabs" role="tablist" aria-label="Sidebar module filters">
            {tabItems.map((item) => (
              <button
                type="button"
                key={item.id}
                className={`sidebar-tab ${tab === item.id ? "active" : ""}`}
                onClick={() => setTab(item.id)}
                role="tab"
                aria-selected={tab === item.id}
              >
                <span>{item.label}</span>
                <b>{item.count}</b>
              </button>
            ))}
          </div>
        )}

        <nav className="sidebar-menu" aria-label="CRM modules">
          {loading ? (
            <SidebarSkeleton collapsed={isCollapsed} />
          ) : moduleError ? (
            <div className={`sidebar-state ${isCollapsed ? "is-collapsed" : ""}`}>
              <ErrorOutlineRoundedIcon />
              {!isCollapsed && (
                <>
                  <strong>Modules unavailable</strong>
                  <span>{moduleError}</span>
                  <button type="button" onClick={loadModules}>Try again</button>
                </>
              )}
            </div>
          ) : groupedItems.length === 0 ? (
            <div className={`sidebar-state ${isCollapsed ? "is-collapsed" : ""}`}>
              <SearchIcon />
              {!isCollapsed && (
                <>
                  <strong>No modules found</strong>
                  <span>{search ? "Try clearing your search." : "No modules match this view."}</span>
                  {search && <button type="button" onClick={handleClearSearch}>Clear search</button>}
                </>
              )}
            </div>
          ) : (
            groupedItems.map((group) => (
              <div className="sidebar-group" key={group.name}>
                {!isCollapsed && <div className="sidebar-group-title">{group.name}</div>}

                {group.items.map((item) => {
                  const Icon = item.Icon || DashboardIcon;
                  const isFavourite = Boolean(item.favourite?.[userEmail]);
                  const isSaving = String(savingFavouriteId) === String(item.id);

                  return (
                    <NavLink
                      key={item.id}
                      to={item.route}
                      data-label={item.displayName}
                      className={({ isActive }) => `sidebar-link ${isActive ? "is-active" : ""}`}
                      title={isCollapsed ? item.displayName : undefined}
                      onClick={() => {
                        if (compactViewport) setCompactExpanded(false);
                      }}
                    >
                      <span className="sidebar-link-icon">
                        <Icon />
                      </span>

                      {!isCollapsed && (
                        <span className="sidebar-link-copy">
                          <span className="sidebar-link-main">
                            <span className="sidebar-link-name">{item.displayName}</span>
                          </span>
                        </span>
                      )}

                      {!isCollapsed && item.canFavourite && (
                        <span
                          role="button"
                          tabIndex={0}
                          className={`sidebar-favourite ${isFavourite ? "is-on" : ""} ${isSaving ? "is-saving" : ""}`}
                          onClick={(event) => toggleFavourite(event, item)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") toggleFavourite(event, item);
                          }}
                          title={isFavourite ? "Remove from favourites" : "Add to favourites"}
                          aria-label={isFavourite ? "Remove from favourites" : "Add to favourites"}
                        >
                          {isFavourite ? <StarIcon /> : <StarBorderIcon />}
                        </span>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            ))
          )}
        </nav>

        <div className={`sidebar-footer ${isCollapsed ? "is-collapsed" : ""}`}>
          <div className="sidebar-user-avatar">{getInitials(userName || userEmail)}</div>

          {!isCollapsed && (
            <div className="sidebar-user-copy">
              <span className="sidebar-user-name" title={userName}>{userName}</span>
              <span className="sidebar-user-meta" title={userEmail}>{titleCaseRole(role)} · {menuItems.length} modules</span>
            </div>
          )}

          {!isCollapsed && <span className="sidebar-user-status" title="Signed in" />}
        </div>
      </div>
      </aside>
    </>
  );
}
